
import { action } from "mobx";
import React, { Component } from "react";

import { Button, CondensedButton } from "@instructure/ui-buttons";
import { Flex } from "@instructure/ui-flex";
import { Heading } from "@instructure/ui-heading";
import { IconAddLine, IconEditLine, IconTrashLine } from "@instructure/ui-icons";
import { Text } from "@instructure/ui-text";
import { View } from "@instructure/ui-view";

import { adapters, showFormPrompt } from "@inst_proserv/finalform-instui";
import { PromptContext, PromptManager, WorkingButton } from "@inst_proserv/toolkit";

interface EditableListProps<T> {
    title: string;
    subject?: string;

    minimum?: number;

    renderEdit: any;
    renderItem?: any;

    values: T[];
    writeOnce?: boolean;
    onChange: (changed_items: T[]) => void;
}

export class EditableList<T> extends Component<EditableListProps<T>> {
    static contextType = PromptContext;
    context: PromptManager;

    @action.bound
    async onAddClicked() {
        const added = await showFormPrompt<T>(this.context, <>{this.props.renderEdit()}</>, {
            title: `Add ${this.props.subject || this.props.title}`,
            saveButton: "Add",
        })
        if (added) {
            this.props.onChange([...this.props.values, added]);
        }
    }

    @action.bound
    async onItemAction(item: T, action) {
        if (action == "edit") {
            const changed = await showFormPrompt<T>(this.context, <>{this.props.renderEdit()}</>, {
                title: `Edit ${this.props.subject || this.props.title}`,
                saveButton: "Done",
                value: item,
            });
            if (changed) this.props.onChange(this.props.values.map(v => v === item ? changed : v));
        } else if (action == "delete") {
            this.props.onChange(this.props.values.filter(v => v !== item));
        }
    }

    render() {
        return <>
            <View as="div">
                <View as="div" borderWidth="0 0 small 0" padding="0 0 xx-small 0" margin="0">
                    <Flex>
                        <Flex.Item shouldGrow>
                            <Heading level="h4" color="primary">{this.props.title}</Heading>
                        </Flex.Item>
                        <Flex.Item>
                            <Button onClick={this.onAddClicked} size="small" withBackground={false} color="primary" renderIcon={<IconAddLine />}></Button>
                        </Flex.Item>
                    </Flex>
                </View>
                {this.props.values.map(v => {
                    return <EditableListItem
                        key={JSON.stringify(v)}
                        onAction={this.onItemAction}
                        value={v}
                        renderItem={this.props.renderItem}
                        allowEdit={!this.props.writeOnce}
                        allowDelete={!this.props.minimum || this.props.values.length > this.props.minimum}
                    />
                })}
            </View>
        </>
    }
}

class EditableListItem extends Component<{ value: any, renderItem?: any, onAction: (item, action: "delete" | "edit") => void, allowEdit?: boolean, allowDelete?: boolean }> {
    @action.bound
    async onEditClicked() {
        await this.props.onAction(this.props.value, "edit");
    }

    @action.bound
    async onDeleteClicked() {
        await this.props.onAction(this.props.value, "delete");
    }

    render() {
        let item = this.props.value;

        if (this.props.renderItem) {
            item = this.props.renderItem(item);
        } else if (typeof item != "string") {
            item = JSON.stringify(item);
        }

        if (typeof item == "string") {
            item = <Text color="primary">{item}</Text>
        }

        return <View as="div" padding="xxx-small 0" borderWidth="0 0 small" >
            <Flex>
                <Flex.Item shouldGrow shouldShrink padding="x-small 0 xx-small">
                    {item}
                </Flex.Item>
                <Flex.Item>
                    {this.props.allowEdit && <WorkingButton component={CondensedButton} size="small" renderIcon={<IconEditLine />} onClick={this.onEditClicked} />}
                    {this.props.allowDelete && <WorkingButton component={CondensedButton} size="small" renderIcon={<IconTrashLine />} onClick={this.onDeleteClicked} />}
                </Flex.Item>
            </Flex>
        </View >
    }
}

adapters.makeAdapter(adapters.FieldAdapter, EditableList, v => v, {
    valueKey: "values",
    valueMap: (v) => v ? v : [],
});
